import type { z } from 'zod';
import { LIMITS } from './common';
import { projectFileSchema, type ProjectFile } from './project';
import { checkFormatCompatibility } from './version';

/** 読み込みの失敗の理由。 */
export type ProjectParseError =
  | { readonly kind: 'not-json'; readonly message: string }
  | { readonly kind: 'too-large'; readonly message: string }
  | { readonly kind: 'unsupported-newer'; readonly message: string; readonly formatVersion: string }
  | { readonly kind: 'unsupported-older'; readonly message: string; readonly formatVersion: string }
  | { readonly kind: 'invalid'; readonly message: string; readonly issues: readonly string[] };

/** 読み込みの結果。 */
export type ProjectParseResult =
  | { readonly ok: true; readonly value: ProjectFile }
  | { readonly ok: false; readonly error: ProjectParseError };

function describeIssues(error: z.ZodError): string[] {
  return error.issues.slice(0, 20).map((issue) => {
    const path = issue.path.map(String).join('.');
    return path === '' ? issue.message : `${path}: ${issue.message}`;
  });
}

/**
 * 保存データ（JSON をパースした値）を検証して、プロジェクトファイルとして読み込む。
 *
 * - 新しいメジャー版は読み込まず、アプリの更新を促す（`unsupported-newer`）
 * - 古いメジャー版は、マイグレーション（版 2 以降で追加）が必要（`unsupported-older`）
 * - スキーマにないフィールドは除去し、省略された任意の項目は既定値で補う
 */
export function parseProjectFile(input: unknown): ProjectParseResult {
  const formatVersion =
    typeof input === 'object' && input !== null && 'formatVersion' in input
      ? input.formatVersion
      : undefined;
  if (typeof formatVersion === 'string' && /^\d+\.\d+\.\d+$/.test(formatVersion)) {
    const compatibility = checkFormatCompatibility(formatVersion);
    if (compatibility === 'unsupported-newer') {
      return {
        ok: false,
        error: {
          kind: 'unsupported-newer',
          formatVersion,
          message:
            'このファイルは新しい版のアプリで作られています。アプリを更新してから開いてください。',
        },
      };
    }
    if (compatibility === 'needs-migration') {
      return {
        ok: false,
        error: {
          kind: 'unsupported-older',
          formatVersion,
          message: 'このファイルは古い形式です。変換に対応していません。',
        },
      };
    }
  }
  const result = projectFileSchema.safeParse(input);
  if (!result.success) {
    return {
      ok: false,
      error: {
        kind: 'invalid',
        message: 'ファイルの内容が正しくありません。',
        issues: describeIssues(result.error),
      },
    };
  }
  return { ok: true, value: result.data };
}

/** 文字列の UTF-8 でのバイト数（`TextEncoder` は DOM 型に属するため使わない）。 */
function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/**
 * JSON 文字列から、プロジェクトファイルを読み込む。
 * 10 MB を超えるデータは、パースする前に拒否する（NFR-SEC-003）。
 */
export function parseProjectJson(text: string): ProjectParseResult {
  if (utf8ByteLength(text) > LIMITS.maxFileBytes) {
    return {
      ok: false,
      error: {
        kind: 'too-large',
        message: `ファイルが大きすぎます（上限 ${String(LIMITS.maxFileBytes / 1024 / 1024)} MB）。`,
      },
    };
  }
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return { ok: false, error: { kind: 'not-json', message: 'JSON として読み込めません。' } };
  }
  return parseProjectFile(json);
}
